import { useNavigate } from "react-router-dom"
import ModalOverlay from "../../../UI/ModalOverlay/ModalOverlay.tsx"
import type { MaterialDetail, MaterialDetailCommerceRow } from "../../../../types/calculadoraDeMateriales.ts"
import { buildProfileUrl } from "../../../../utils/navigationHelpers.ts"
import styles from "./MaterialDetailModal.module.scss"

type MaterialDetailModalProps = {
  isOpen: boolean
  onClose: () => void
  isLoading: boolean
  error: string | null
  detail: MaterialDetail | null
  onRetry: () => void
}

const formatCurrencyArs = (value: number | null | undefined) => {
  if (typeof value !== "number" || !Number.isFinite(value)) return "No informado"
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(value)
}

const resolveCommerceOwnerUserId = (row: MaterialDetailCommerceRow): string | null => {
  const rowRec = row as MaterialDetailCommerceRow & { owner_user_id?: number | string | null }
  const commerceRec = row.commerce as
    | (NonNullable<MaterialDetailCommerceRow["commerce"]> & { owner_user_id?: number | string | null })
    | null
    | undefined
  const raw =
    rowRec.ownerUserId ??
    rowRec.owner_user_id ??
    commerceRec?.ownerUserId ??
    commerceRec?.owner_user_id ??
    commerceRec?.user_id ??
    commerceRec?.userId
  if (raw == null) return null
  const s = String(raw).trim()
  if (!s || s.toLowerCase() === "null") return null
  return s
}

const resolveCommerceIdNumber = (row: MaterialDetailCommerceRow): number | null => {
  const raw = row.commerce?.id
  if (raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

const MaterialDetailModal = ({
  isOpen,
  onClose,
  isLoading,
  error,
  detail,
  onRetry,
}: MaterialDetailModalProps) => {
  const navigate = useNavigate()
  const panel = (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <h2 className={styles.title}>{detail?.name ?? "Detalle de material"}</h2>
        <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Cerrar detalle">
          ×
        </button>
      </div>
      <div className={styles.content}>
        {isLoading ? (
          <div className={styles.skeletonRoot}>
            <div className={`${styles.skeletonBlock} ${styles.skeletonMeta}`} />
            <div className={`${styles.skeletonBlock} ${styles.skeletonText}`} />
            <div className={`${styles.skeletonBlock} ${styles.skeletonList}`} />
          </div>
        ) : null}

        {!isLoading && error ? (
          <div className={styles.error}>
            <div>{error}</div>
            <button type="button" className={styles.retryBtn} onClick={onRetry}>
              Reintentar
            </button>
          </div>
        ) : null}

        {!isLoading && !error && detail ? (
          <>
            <div className={styles.meta}>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Marca</span>
                <span className={styles.metaValue}>
                  {detail.brand?.trim() ? detail.brand : "No informada"}
                </span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Unidad</span>
                <span className={styles.metaValue}>
                  {detail.unit?.trim() ? detail.unit : "No informada"}
                </span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Precio estimado</span>
                <span className={styles.metaValue}>{formatCurrencyArs(detail.price)}</span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Categoría</span>
                <span className={styles.metaValue}>
                  {detail.category?.name?.trim() ? detail.category.name : "No informada"}
                </span>
              </div>
            </div>

            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Descripción</h3>
              <p className={styles.bodyText}>{detail.description?.trim() ? detail.description : "Sin descripción"}</p>
            </section>

            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Comercios que lo tienen listado</h3>
              {detail.materials_commerce && detail.materials_commerce.length > 0 ? (
                <div className={styles.commerceList}>
                  {detail.materials_commerce.map((row) => {
                    const ownerUserId = resolveCommerceOwnerUserId(row)
                    const commerceIdNum = resolveCommerceIdNumber(row)
                    const commerceName = row.commerce?.name?.trim() ? row.commerce.name : null
                    const profileUrl =
                      ownerUserId != null ? buildProfileUrl(ownerUserId, commerceName, "Resumen") : null
                    const catalogUrl =
                      ownerUserId != null && commerceIdNum != null
                        ? buildProfileUrl(ownerUserId, commerceName, "Materiales", {
                            commerceId: commerceIdNum,
                          })
                        : null
                    const hasCommerceLinks = profileUrl != null || catalogUrl != null
                    return (
                      <article className={styles.commerceItem} key={row.id}>
                        <p className={styles.commerceName}>{row.commerce?.name ?? "Comercio sin nombre"}</p>
                        <div className={styles.commerceFacts}>
                          <div className={styles.commerceInfo}>
                            <span className={styles.commerceLabel}>Zona</span>
                            <span className={styles.commerceValue}>{row.commerce?.zone ?? "No informada"}</span>
                          </div>
                          <div className={styles.commerceInfo}>
                            <span className={styles.commerceLabel}>Precio</span>
                            <span className={styles.commerceValue}>{formatCurrencyArs(row.price)}</span>
                          </div>
                          <div className={styles.commerceInfo}>
                            <span className={styles.commerceLabel}>Promoción</span>
                            <span className={styles.commerceValue}>
                              {row.promotion?.trim() ? row.promotion : "Sin promoción"}
                            </span>
                          </div>
                          <div className={styles.commerceInfo}>
                            <span className={styles.commerceLabel}>IVA</span>
                            <span className={styles.commerceValue}>
                              {row.value_added_tax === true
                                ? "Incluido"
                                : row.value_added_tax === false
                                  ? "No incluido"
                                  : "No informado"}
                            </span>
                          </div>
                        </div>
                        {hasCommerceLinks ? (
                          <div className={styles.commerceRowLinks}>
                            {profileUrl ? (
                              <button
                                type="button"
                                className={styles.commerceLink}
                                onClick={() => {
                                  onClose()
                                  navigate(profileUrl)
                                }}
                              >
                                Ver perfil
                              </button>
                            ) : null}
                            {catalogUrl ? (
                              <button
                                type="button"
                                className={styles.commerceLink}
                                onClick={() => {
                                  onClose()
                                  navigate(catalogUrl)
                                }}
                              >
                                Catálogo en perfil
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </article>
                    )
                  })}
                </div>
              ) : (
                <p className={styles.empty}>
                  No hay comercios listados para este material, los datos del material corresponden al relevamiento del
                  mercado realizado por Edify.
                </p>
              )}
            </section>
          </>
        ) : null}
      </div>
    </div>
  )

  if (!isOpen) return null

  return (
    <ModalOverlay isOpen={isOpen} onClose={onClose} zIndex={6}>
      {panel}
    </ModalOverlay>
  )
}

export default MaterialDetailModal
